import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Building2, Users, Headphones } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface StatItemProps {
  value: number;
  suffix?: string;
  label: string;
  icon: React.ElementType;
  delay: number;
}

const StatItem = ({ value, suffix = "", label, icon: Icon, delay }: StatItemProps) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || hasAnimated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            
            const duration = 2000;
            const steps = 60;
            const increment = value / steps;
            let current = 0;

            const timer = setTimeout(() => {
              const interval = setInterval(() => {
                current += increment;
                if (current >= value) {
                  setCount(value);
                  clearInterval(interval);
                } else {
                  setCount(Math.floor(current));
                }
              }, duration / steps);
            }, delay);

            return () => clearTimeout(timer);
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [value, delay, hasAnimated]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: delay / 1000 }}
      className="relative group"
    >
      <div className="bg-card border border-border rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
        {/* Icon */}
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4 group-hover:scale-110 transition-transform">
          <Icon className="w-8 h-8" />
        </div>
        
        {/* Number */}
        <div className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-2">
          <span className="tabular-nums">{count}</span>
          <span className="text-primary">{suffix}</span>
        </div>
        
        {/* Label */}
        <p className="text-muted-foreground font-medium">{label}</p>
        
        {/* Decorative element */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-1 bg-primary/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </motion.div>
  );
};

const AnimatedStats = () => {
  const { t } = useLanguage();

  const stats = [
    { value: 90, suffix: "+", label: t("stats.years"), icon: Calendar },
    { value: 1000, suffix: "+", label: t("stats.installations"), icon: Building2 },
    { value: 5000, suffix: "+", label: t("stats.clients"), icon: Users },
    { value: 24, suffix: "/7", label: t("stats.support"), icon: Headphones },
  ];

  return (
    <section className="py-20 bg-muted/50">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t("stats.title")} <span className="text-primary">{t("stats.titleHighlight")}</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t("stats.subtitle")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <StatItem
              key={stat.label}
              {...stat}
              delay={index * 200}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AnimatedStats;
